/**
 * Coduri QR — gestionarea codurilor tipărite pentru mese, bar, terasă, flyere.
 *
 * Ideea centrală, vizibilă în toată pagina: codul conține un URL permanent, nu meniul. Poți
 * schimba meniul, prețurile, chiar și eticheta codului — codul tipărit rămâne același. De aceea
 * pagina pune accent pe: previzualizarea exactă a ceea ce se tipărește, descărcarea în PNG/SVG,
 * fișa de print și statisticile de scanare, ca proprietarul să știe ce funcționează.
 */
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  InlineAlert,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  cx,
  useToast,
} from '../components/ui.js';
import { api } from '../lib/api.js';
import { formatDateTime, relativeTime, useResource } from '../lib/hooks.js';
import { errorMessage, fieldErrors, useSession } from '../lib/session.js';

interface QrCode {
  id: string;
  public_id: string;
  code: string;
  label: string;
  purpose: string;
  status: 'active' | 'paused' | 'archived';
  tracking_enabled: boolean;
  target_url: string;
  style: { fg: string; bg: string; ec: 'L' | 'M' | 'Q' | 'H'; margin: number };
  scan_count: number;
  last_scanned_at: string | null;
  restaurant_id: string;
  restaurant_name: string;
  location_id: string | null;
  location_name: string | null;
  menu_id: string | null;
  menu_name: string | null;
  scans_30d: number;
  unique_30d: number;
  preview_svg?: string;
}

interface Overview {
  codes: number;
  active: number;
  scans: number;
  unique: number;
  daily: Array<{ day: string; scans: number }>;
  days: number;
}

interface Stats {
  totals: { scans: number; unique: number; last30: number; last7: number };
  daily: Array<{ day: string; scans: number; unique: number }>;
  devices: Array<{ name: string; count: number }>;
  hours: number[];
  topLocales: Array<{ locale: string; count: number }>;
}

interface Restaurant {
  id: string;
  name: string;
}
interface Location {
  id: string;
  name: string;
  restaurant_id: string;
  is_active: boolean;
}
interface Menu {
  id: string;
  name: string;
  restaurant_id: string;
  status: string;
}

const PURPOSES: Record<string, string> = {
  table: 'Masă',
  bar: 'Bar',
  terrace: 'Terasă',
  reception: 'Recepție',
  flyer: 'Flyer / delivery',
  packaging: 'Ambalaj',
  window: 'Vitrină',
};

const STATUS: Record<QrCode['status'], { label: string; tone: 'success' | 'warning' | 'neutral' }> = {
  active: { label: 'activ', tone: 'success' },
  paused: { label: 'în pauză', tone: 'warning' },
  archived: { label: 'arhivat', tone: 'neutral' },
};

/** Palete gata pregătite: contrastul este verificat, deci codul se citește sigur. */
const PALETTES: Array<{ name: string; fg: string; bg: string }> = [
  { name: 'Clasic', fg: '#0B1220', bg: '#FFFFFF' },
  { name: 'Verde Menuvia', fg: '#0F3D33', bg: '#FFFFFF' },
  { name: 'Negru pe crem', fg: '#161311', bg: '#FBF7F0' },
  { name: 'Burgund', fg: '#5B1A22', bg: '#FFFBF8' },
  { name: 'Albastru', fg: '#12335B', bg: '#F7FAFF' },
];

export function QrPage() {
  const session = useSession();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [restaurantFilter, setRestaurantFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [printOpen, setPrintOpen] = useState(false);
  // Formatul de tipărire: cum se folosește codul (pe masă, lipit, pe vitrină, flyer) + hârtia.
  const [printForm, setPrintForm] = useState<{ layout: string; size: string }>({ layout: 'table_tent', size: 'a4' });
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<QrCode | null>(null);
  const [statsFor, setStatsFor] = useState<QrCode | null>(null);
  const [deleting, setDeleting] = useState<QrCode | null>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [form, setForm] = useState({
    label: '',
    code: '',
    purpose: 'table',
    restaurantId: '',
    locationId: '',
    menuId: '',
    trackingEnabled: true,
    fg: '#0B1220',
    bg: '#FFFFFF',
    ec: 'M' as 'L' | 'M' | 'Q' | 'H',
  });

  const query = new URLSearchParams();
  if (restaurantFilter) query.set('restaurantId', restaurantFilter);
  if (statusFilter) query.set('status', statusFilter);
  if (search.trim()) query.set('search', search.trim());

  const codes = useResource<QrCode[]>(() => api.get<QrCode[]>(`/qr?${query.toString()}`), [
    search,
    restaurantFilter,
    statusFilter,
    session.activeAccountId,
  ]);
  const overview = useResource<Overview>(async () => {
    const response = await api.getRaw<{ data: QrCode[]; meta: { overview: Overview } }>(`/qr?days=30`);
    return response.meta.overview;
  }, [session.activeAccountId]);

  const restaurants = useResource<Restaurant[]>(() => api.get<Restaurant[]>('/restaurants'), [session.activeAccountId]);
  const locations = useResource<Location[]>(() => api.get<Location[]>('/locations'), [session.activeAccountId]);
  const menus = useResource<Menu[]>(() => api.get<Menu[]>('/menus'), [session.activeAccountId]);
  const stats = useResource<Stats | null>(
    () => (statsFor ? api.get<Stats>(`/qr/${statsFor.id}/stats?days=30`) : Promise.resolve(null)),
    [statsFor?.id],
  );

  const list = codes.data ?? [];
  const canCreate = session.can('qr.create');
  const canUpdate = session.can('qr.update');
  const canDelete = session.can('qr.delete');
  const canDownload = session.can('qr.download');

  const totals = useMemo(
    () => ({
      scans: overview.data?.scans ?? 0,
      unique: overview.data?.unique ?? 0,
      best: list.reduce<QrCode | null>((best, item) => (best && best.scans_30d >= item.scans_30d ? best : item), null),
      silent: list.filter((item) => item.status === 'active' && item.scans_30d === 0).length,
    }),
    [overview.data, list],
  );

  function openCreate() {
    setForm({
      label: '',
      code: '',
      purpose: 'table',
      restaurantId: restaurants.data?.[0]?.id ?? '',
      locationId: '',
      menuId: '',
      trackingEnabled: true,
      fg: '#0B1220',
      bg: '#FFFFFF',
      ec: 'M',
    });
    setErrors({});
    setCreateOpen(true);
  }

  function openEdit(code: QrCode) {
    setForm({
      label: code.label,
      code: code.code,
      purpose: code.purpose,
      restaurantId: code.restaurant_id,
      locationId: code.location_id ?? '',
      menuId: code.menu_id ?? '',
      trackingEnabled: code.tracking_enabled,
      fg: code.style?.fg ?? '#0B1220',
      bg: code.style?.bg ?? '#FFFFFF',
      ec: code.style?.ec ?? 'M',
    });
    setErrors({});
    setEditing(code);
  }

  async function save() {
    setBusy(true);
    setErrors({});
    try {
      const style = { fg: form.fg, bg: form.bg, ec: form.ec };
      if (editing) {
        await api.patch(`/qr/${editing.id}`, {
          label: form.label.trim(),
          code: form.code.trim() || undefined,
          purpose: form.purpose,
          locationId: form.locationId || null,
          menuId: form.menuId || null,
          trackingEnabled: form.trackingEnabled,
          style,
        });
        toast.success('Codul QR a fost actualizat', 'Codul tipărit rămâne valabil — se schimbă doar ce vezi în dashboard.');
        setEditing(null);
      } else {
        await api.post('/qr', {
          label: form.label.trim(),
          code: form.code.trim() || undefined,
          purpose: form.purpose,
          restaurantId: form.restaurantId,
          locationId: form.locationId || null,
          menuId: form.menuId || null,
          trackingEnabled: form.trackingEnabled,
          style,
        });
        toast.success('Codul QR a fost creat', 'Îl poți descărca la rezoluție de tipar sau tipări direct din fișa de print.');
        setCreateOpen(false);
      }
      await codes.reload();
      await overview.reload();
    } catch (error) {
      const fields = fieldErrors(error);
      setErrors(fields);
      toast.error('Codul QR nu a putut fi salvat', Object.keys(fields).length ? Object.values(fields)[0]?.[0] : errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function toggleStatus(code: QrCode) {
    const next = code.status === 'active' ? 'paused' : 'active';
    try {
      await api.patch(`/qr/${code.id}`, { status: next });
      toast.success(next === 'active' ? `Codul ${code.code} este din nou activ` : `Codul ${code.code} a fost pus în pauză`);
      await codes.reload();
    } catch (error) {
      toast.error('Starea nu a putut fi schimbată', errorMessage(error));
    }
  }

  async function remove() {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.del(`/qr/${deleting.id}`);
      toast.success(`Codul ${deleting.code} a fost șters`);
      setDeleting(null);
      await codes.reload();
      await overview.reload();
    } catch (error) {
      toast.error('Codul nu a putut fi șters', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  function download(code: QrCode, format: 'png' | 'svg') {
    const url = `/api/v1/qr/${code.id}/image?format=${format}&size=2048&download=1`;
    // Descărcarea trece prin API (cu sesiune), deci deschidem într-un tab nou, nu cu un link direct.
    window.open(url, '_blank', 'noopener');
    toast.success(`Se descarcă ${code.code}.${format.toUpperCase()}`, 'PNG la 2048 px pentru tipar rapid; SVG pentru tipografie.');
  }

  function printSheet() {
    const ids = selected.join(',');
    window.open(`/api/v1/qr/print?ids=${ids}&layout=${printForm.layout}&size=${printForm.size}`, '_blank', 'noopener');
    setPrintOpen(false);
  }

  return (
    <div className="page-enter">
      <PageHeader
        title="Coduri QR"
        subtitle="Codul tipărit rămâne același la nesfârșit: conține adresa meniului, nu meniul. Modificările ajung instant pe toate mesele."
        actions={
          <div className="flex items-center gap-2">
            {selected.length ? (
              <Button
                variant="secondary"
                icon={<Icon name="grid" size={16} />}
                onClick={() => setPrintOpen(true)}
              >
                Tipărește selecția ({selected.length})
              </Button>
            ) : null}
            {canCreate ? (
              <Button icon={<Icon name="plus" size={17} />} onClick={openCreate}>
                Cod QR nou
              </Button>
            ) : null}
          </div>
        }
      />

      {/* Indicatori */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="flex flex-col gap-1">
          <span className="text-caption uppercase tracking-wide text-ink-400">Coduri active</span>
          <span className="font-display text-[26px] leading-none text-ink-950 nums">
            {overview.data?.active ?? '—'}
            <span className="ml-1 text-[15px] text-ink-400">/ {overview.data?.codes ?? 0}</span>
          </span>
        </Card>
        <Card className="flex flex-col gap-1">
          <span className="text-caption uppercase tracking-wide text-ink-400">Scanări · 30 zile</span>
          <span className="font-display text-[26px] leading-none text-ink-950 nums">{totals.scans}</span>
        </Card>
        <Card className="flex flex-col gap-1">
          <span className="text-caption uppercase tracking-wide text-ink-400">Vizitatori unici</span>
          <span className="font-display text-[26px] leading-none text-ink-950 nums">{totals.unique}</span>
          <span className="text-caption text-ink-400">fără date personale</span>
        </Card>
        <Card className="flex flex-col gap-1">
          <span className="text-caption uppercase tracking-wide text-ink-400">Cod cel mai folosit</span>
          <span className="truncate text-body-l text-ink-950">{totals.best?.code ?? '—'}</span>
          <span className="text-caption text-ink-400 nums">{totals.best ? `${totals.best.scans_30d} scanări` : ''}</span>
        </Card>
      </div>

      {totals.silent > 0 ? (
        <div className="mb-4">
          <InlineAlert tone="warning" title={`${totals.silent} coduri active nu au fost scanate în ultimele 30 de zile`}>
            Merită verificate: poziția pe masă, dimensiunea la tipărire sau contrastul codului.
          </InlineAlert>
        </div>
      ) : null}

      {/* Filtre */}
      <Card className="mb-4 flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Icon name="search" size={17} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-400" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Caută după etichetă sau cod (ex. MASA-01)…"
            className="pl-9"
            aria-label="Caută coduri QR"
          />
        </div>
        <Select
          value={restaurantFilter}
          onChange={(event) => setRestaurantFilter(event.target.value)}
          className="sm:w-48"
          aria-label="Filtrează după restaurant"
        >
          <option value="">Toate restaurantele</option>
          {(restaurants.data ?? []).map((restaurant) => (
            <option key={restaurant.id} value={restaurant.id}>
              {restaurant.name}
            </option>
          ))}
        </Select>
        <Select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className="sm:w-40"
          aria-label="Filtrează după stare"
        >
          <option value="">Toate stările</option>
          <option value="active">Active</option>
          <option value="paused">În pauză</option>
          <option value="archived">Arhivate</option>
        </Select>
      </Card>

      {/* Listă */}
      {codes.loading ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-52" />
          ))}
        </div>
      ) : codes.error ? (
        <InlineAlert tone="warning" title="Codurile nu au putut fi încărcate">
          {codes.error}{' '}
          <button type="button" className="underline" onClick={() => void codes.reload()}>
            Reîncearcă
          </button>
        </InlineAlert>
      ) : !list.length ? (
        <EmptyState
          icon={<Icon name="qr" size={26} />}
          title={search || statusFilter ? 'Niciun cod pentru filtrele curente' : 'Niciun cod QR încă'}
          description="Creează coduri pentru fiecare masă, pentru bar, terasă sau flyere. Le tipărești o dată și le folosești ani de zile."
          action={canCreate ? <Button onClick={openCreate}>Creează primul cod</Button> : null}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {list.map((code) => (
            <Card key={code.id} className="flex gap-4">
              <div className="flex flex-col items-center gap-2">
                <div
                  className={cx(
                    'transition-ui rounded-xl border border-ink-100 bg-white p-2',
                    selected.includes(code.id) ? 'ring-2 ring-brand ring-offset-2' : '',
                  )}
                  // Previzualizarea este SVG-ul care se tipărește, la scară mică.
                  dangerouslySetInnerHTML={{ __html: code.preview_svg ?? '' }}
                  style={{ width: 92, height: 92 }}
                />
                <label className="flex cursor-pointer items-center gap-1.5 text-caption text-ink-600">
                  <input
                    type="checkbox"
                    className="accent-[var(--brand)]"
                    checked={selected.includes(code.id)}
                    onChange={(event) =>
                      setSelected((current) =>
                        event.target.checked ? [...current, code.id] : current.filter((id) => id !== code.id),
                      )
                    }
                  />
                  pentru print
                </label>
              </div>

              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink-950">{code.label}</span>
                      <Badge tone={STATUS[code.status].tone}>{STATUS[code.status].label}</Badge>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-ink-400">
                      <span className="font-mono">{code.code}</span>
                      <span>{PURPOSES[code.purpose] ?? code.purpose}</span>
                      {code.location_name ? <span>{code.location_name}</span> : null}
                      {code.menu_name ? <span>meniu: {code.menu_name}</span> : null}
                    </div>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2 border-y border-ink-100 py-2.5">
                  <div className="flex flex-col">
                    <span className="text-caption text-ink-400">Scanări (30z)</span>
                    <span className="text-body-s font-medium text-ink-950 nums">{code.scans_30d}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-caption text-ink-400">Unici</span>
                    <span className="text-body-s font-medium text-ink-950 nums">{code.unique_30d}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-caption text-ink-400">Ultima scanare</span>
                    <span className="text-body-s text-ink-600">
                      {code.last_scanned_at ? relativeTime(code.last_scanned_at) : 'niciodată'}
                    </span>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setStatsFor(code)} icon={<Icon name="chart" size={14} />}>
                    Statistici
                  </Button>
                  {canDownload ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => download(code, 'png')}>
                        PNG
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => download(code, 'svg')}>
                        SVG
                      </Button>
                    </>
                  ) : null}
                  {canUpdate ? (
                    <Button variant="ghost" size="sm" onClick={() => openEdit(code)}>
                      Editează
                    </Button>
                  ) : null}
                  <span className="ml-auto flex items-center gap-1">
                    {canUpdate ? (
                      <Button variant="ghost" size="sm" onClick={() => void toggleStatus(code)}>
                        {code.status === 'active' ? 'Pune în pauză' : 'Reactivează'}
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button variant="ghost" size="sm" className="text-danger" onClick={() => setDeleting(code)}>
                        Șterge
                      </Button>
                    ) : null}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Creare / editare */}
      <Modal
        open={createOpen || Boolean(editing)}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
        }}
        title={editing ? `Editează ${editing.code}` : 'Cod QR nou'}
        description={
          editing
            ? 'Codul din URL nu se schimbă: poți redenumi eticheta, muta codul la altă masă sau schimba meniul afișat, fără să retipărești nimic.'
            : 'Alege unde stă codul și ce meniu deschide. Îl poți tipări imediat după creare.'
        }
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setCreateOpen(false);
                setEditing(null);
              }}
            >
              Anulează
            </Button>
            <Button loading={busy} disabled={form.label.trim().length < 2 || !form.restaurantId} onClick={() => void save()}>
              {editing ? 'Salvează' : 'Creează codul'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Etichetă" hint="Ce scrie pe cartonaș: „Masa 1”, „Terasă 3”, „Bar”." required errors={errors.label}>
              {(props) => (
                <Input
                  {...props}
                  value={form.label}
                  onChange={(event) => setForm({ ...form, label: event.target.value })}
                  placeholder="Masa 1"
                />
              )}
            </Field>
            <Field label="Cod intern" hint="Opțional — se generează automat (MASA-01)." errors={errors.code}>
              {(props) => (
                <Input
                  {...props}
                  value={form.code}
                  onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })}
                  placeholder="MASA-01"
                  className="font-mono"
                />
              )}
            </Field>
            <Field label="Tip de loc">
              {(props) => (
                <Select {...props} value={form.purpose} onChange={(event) => setForm({ ...form, purpose: event.target.value })}>
                  {Object.entries(PURPOSES).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Restaurant" required errors={errors.restaurantId}>
              {(props) => (
                <Select
                  {...props}
                  value={form.restaurantId}
                  onChange={(event) => setForm({ ...form, restaurantId: event.target.value, locationId: '', menuId: '' })}
                  disabled={Boolean(editing)}
                >
                  <option value="">Alege restaurantul</option>
                  {(restaurants.data ?? []).map((restaurant) => (
                    <option key={restaurant.id} value={restaurant.id}>
                      {restaurant.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Locație" hint="Opțional. Locațiile se adaugă, editează și șterg din meniul Locații.">
              {(props) => (
                <Select {...props} value={form.locationId} onChange={(event) => setForm({ ...form, locationId: event.target.value })}>
                  <option value="">Toate locațiile</option>
                  {(locations.data ?? [])
                    .filter((location) => location.restaurant_id === form.restaurantId && (location.is_active || location.id === form.locationId))
                    .map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.is_active ? location.name : `${location.name} (inactivă)`}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
            <Field label="Meniu deschis" hint="Implicit: meniul publicat cel mai recent.">
              {(props) => (
                <Select {...props} value={form.menuId} onChange={(event) => setForm({ ...form, menuId: event.target.value })}>
                  <option value="">Meniul publicat implicit</option>
                  {(menus.data ?? [])
                    .filter((menu) => menu.restaurant_id === form.restaurantId)
                    .map((menu) => (
                      <option key={menu.id} value={menu.id}>
                        {menu.name}
                        {menu.status === 'published' ? '' : ' (nepublicat)'}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
          </div>

          {/* Aspectul codului */}
          <div className="rounded-xl border border-ink-100 p-4">
            <p className="mb-3 text-body-s font-medium text-ink-950">Aspect la tipărire</p>
            <div className="flex flex-wrap gap-2">
              {PALETTES.map((palette) => (
                <button
                  key={palette.name}
                  type="button"
                  onClick={() => setForm({ ...form, fg: palette.fg, bg: palette.bg })}
                  className={cx(
                    'transition-ui flex items-center gap-2 rounded-lg border px-3 py-2 text-caption',
                    form.fg === palette.fg && form.bg === palette.bg
                      ? 'border-brand bg-brand-soft text-brand'
                      : 'border-ink-200 text-ink-600 hover:border-ink-300',
                  )}
                >
                  <span
                    className="inline-block h-4 w-4 rounded"
                    style={{ background: palette.fg, boxShadow: `0 0 0 2px ${palette.bg}, 0 0 0 3px rgba(11,18,32,.12)` }}
                  />
                  {palette.name}
                </button>
              ))}
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <Field label="Culoare cod">
                {(props) => (
                  <input
                    {...props}
                    type="color"
                    value={form.fg}
                    onChange={(event) => setForm({ ...form, fg: event.target.value })}
                    className="h-10 w-full cursor-pointer rounded-lg border border-ink-200 bg-white p-1"
                    aria-label="Culoarea codului"
                  />
                )}
              </Field>
              <Field label="Culoare fundal">
                {(props) => (
                  <input
                    {...props}
                    type="color"
                    value={form.bg}
                    onChange={(event) => setForm({ ...form, bg: event.target.value })}
                    className="h-10 w-full cursor-pointer rounded-lg border border-ink-200 bg-white p-1"
                    aria-label="Culoarea fundalului"
                  />
                )}
              </Field>
              <Field label="Corecție erori" hint="H = rezistă la murdărire/uzură.">
                {(props) => (
                  <Select
                    {...props}
                    value={form.ec}
                    onChange={(event) => setForm({ ...form, ec: event.target.value as 'L' | 'M' | 'Q' | 'H' })}
                  >
                    <option value="L">L — 7%</option>
                    <option value="M">M — 15% (recomandat)</option>
                    <option value="Q">Q — 25%</option>
                    <option value="H">H — 30%</option>
                  </Select>
                )}
              </Field>
            </div>
            <p className="mt-3 text-caption text-ink-400">
              Verifică contrastul: cu cât fundalul și codul sunt mai diferite, cu atât scanarea e mai rapidă.
            </p>
          </div>

          <Switch
            checked={form.trackingEnabled}
            onChange={(value) => setForm({ ...form, trackingEnabled: value })}
            label="Măsoară scanările"
            description="Numără scanările și tipul de dispozitiv, fără date personale (fără IP, fără cookie-uri)."
          />
        </div>
      </Modal>

      {/* Statistici */}
      <Modal
        open={Boolean(statsFor)}
        onClose={() => setStatsFor(null)}
        title={`Statistici · ${statsFor?.label ?? ''}`}
        description={`Codul ${statsFor?.code ?? ''} · ultimele 30 de zile.`}
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => setStatsFor(null)}>
            Închide
          </Button>
        }
      >
        {stats.loading ? (
          <Skeleton className="h-64" />
        ) : !stats.data || stats.data.totals.scans === 0 ? (
          <EmptyState
            icon={<Icon name="chart" size={24} />}
            title="Încă nicio scanare"
            description="După ce codul este tipărit și așezat pe masă, aici apar scanările pe zile, ore și tipuri de dispozitiv."
          />
        ) : (
          <div className="flex flex-col gap-5">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { label: 'Total scanări', value: stats.data.totals.scans },
                { label: 'Vizitatori unici', value: stats.data.totals.unique },
                { label: 'Ultimele 30 zile', value: stats.data.totals.last30 },
                { label: 'Ultimele 7 zile', value: stats.data.totals.last7 },
              ].map((item) => (
                <div key={item.label} className="rounded-xl bg-surface-2 p-3">
                  <div className="text-caption text-ink-400">{item.label}</div>
                  <div className="font-display text-[22px] leading-tight text-ink-950 nums">{item.value}</div>
                </div>
              ))}
            </div>

            <div>
              <p className="mb-2 text-body-s font-medium text-ink-950">Scanări pe zile</p>
              <div className="flex h-24 items-end gap-1">
                {stats.data.daily.length ? (
                  stats.data.daily.map((day) => {
                    const max = Math.max(...stats.data!.daily.map((item) => item.scans), 1);
                    return (
                      <div
                        key={day.day}
                        title={`${day.day}: ${day.scans} scanări`}
                        className="flex-1 rounded-t bg-brand/80"
                        style={{ height: `${Math.max((day.scans / max) * 100, 4)}%` }}
                      />
                    );
                  })
                ) : (
                  <p className="text-caption text-ink-400">fără date în interval</p>
                )}
              </div>
            </div>

            <div>
              <p className="mb-2 text-body-s font-medium text-ink-950">Distribuție pe ore</p>
              <div className="flex h-20 items-end gap-0.5">
                {stats.data.hours.map((value, hour) => {
                  const max = Math.max(...stats.data!.hours, 1);
                  return (
                    <div
                      key={hour}
                      title={`${hour}:00 — ${value} scanări`}
                      className={cx('flex-1 rounded-t', value > 0 ? 'bg-brand/70' : 'bg-ink-100')}
                      style={{ height: `${Math.max((value / max) * 100, 3)}%` }}
                    />
                  );
                })}
              </div>
              <div className="mt-1 flex justify-between text-caption text-ink-400">
                <span>00:00</span>
                <span>12:00</span>
                <span>23:00</span>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-body-s font-medium text-ink-950">Dispozitive</p>
                <ul className="flex flex-col gap-1.5">
                  {stats.data.devices.map((device) => (
                    <li key={device.name} className="flex items-center justify-between text-body-s">
                      <span className="text-ink-600 capitalize">{device.name}</span>
                      <span className="nums text-ink-950">{device.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="mb-2 text-body-s font-medium text-ink-950">Limbi</p>
                <ul className="flex flex-col gap-1.5">
                  {stats.data.topLocales.map((locale) => (
                    <li key={locale.locale} className="flex items-center justify-between text-body-s">
                      <span className="text-ink-600 uppercase">{locale.locale}</span>
                      <span className="nums text-ink-950">{locale.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <p className="text-caption text-ink-400">
              Măsurarea nu colectează date personale: nu se salvează adresa IP, nu se folosesc cookie-uri, iar
              „vizitatorul unic” este un identificator anonim care se schimbă în fiecare zi (GDPR).
            </p>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Ștergi codul QR?"
        description={`Codul ${deleting?.code ?? ''} (${deleting?.label ?? ''}) nu va mai funcționa. Dacă este deja tipărit și așezat pe masă, clienții vor vedea o pagină care îi îndrumă. Folosește „Pune în pauză” dacă vrei să poți reveni.`}
        confirmLabel="Șterge codul"
        tone="danger"
        loading={busy}
        onClose={() => setDeleting(null)}
        onConfirm={() => void remove()}
      />
      <Modal
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        title="Tipărește codurile selectate"
        description={`${selected.length} coduri. Alegerea formatului schimbă mărimea și instrucțiunile de pe fișă.`}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPrintOpen(false)}>
              Renunță
            </Button>
            <Button icon={<Icon name="grid" size={16} />} onClick={printSheet}>
              Deschide fișa de tipărire
            </Button>
          </div>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Cum se folosește codul">
            {(props) => (
              <Select {...props} value={printForm.layout} onChange={(event) => setPrintForm({ ...printForm, layout: event.target.value })}>
                <option value="table_tent">Pe masă (cartonaș în picioare)</option>
                <option value="sticker">Autocolant mic, lipit pe masă</option>
                <option value="poster">Afiș la intrare sau pe vitrină</option>
                <option value="flyer">Flyer pentru livrare</option>
              </Select>
            )}
          </Field>
          <Field label="Hârtie">
            {(props) => (
              <Select {...props} value={printForm.size} onChange={(event) => setPrintForm({ ...printForm, size: event.target.value })}>
                <option value="a4">A4 (tipărire acasă / birou)</option>
                <option value="a5">A5 (jumătate de coală)</option>
                <option value="a3">A3 (afiș mare)</option>
              </Select>
            )}
          </Field>
        </div>
        <p className="mt-4 text-caption text-ink-500">
          Fișa se deschide într-un tab nou, cu ghidaje de tăiere. Din dialogul de tipărire alege „Salvează ca PDF”
          dacă vrei să trimiți fișierul la tipografie.
        </p>
      </Modal>
    </div>
  );
}
